<script setup lang="ts">
/**
 * NotificationDetailModal — Shows full product request detail with approve/reject actions.
 *
 * Fields:
 *   - review_notes (optional; sends empty string if not provided)
 *   - internal_notes (optional)
 *   - approved_product_name (required for approve)
 *   - rejection_reason (required dropdown for reject)
 *   - rejection_detail (required for reject)
 */
import { ref, computed, watch } from 'vue';
import { X, Loader2, CheckCircle, XCircle, Eye } from 'lucide-vue-next';
import Button from 'primevue/button';
import Select from 'primevue/select';
import InputText from 'primevue/inputtext';
import Textarea from 'primevue/textarea';
import { useNotificationsStore } from '@/stores/notifications.store';
import { useAuthStore } from '@/stores';
import { useToaster, useTimezone } from '@tms-phoenix/ui-vue';
import { REJECTION_REASONS } from '@tms-phoenix/tms-types';
import type { ActionProductRequest } from '@tms-phoenix/tms-types';
import ProductViewDialog from '@/modules/products/components/ProductViewDialog.vue';
import type { Product } from '@/modules/products/types';
import type { ApiProductDetail } from '@/modules/products/types/product-api.types';
import { apiDetailToFormPayload } from '@/modules/products/services/product.transforms';

const store = useNotificationsStore();
const authStore = useAuthStore();
const toast = useToaster();

// Form state
const reviewNotes = ref('');
const internalNotes = ref('');
const approvedProductName = ref('');
const rejectionReason = ref<(typeof REJECTION_REASONS)[number] | ''>('');
const rejectionDetail = ref('');
const fieldErrors = ref<Record<string, string>>({});

// Product view dialog state
const productViewVisible = ref(false);
const productViewData = computed<Product | null>(() => {
  const req = store.selectedRequest;
  if (!req) return null;
  const data = (req.product_data ?? {}) as Record<string, unknown>;
  // Normalize older snapshot keys (brand/form/manufacturer/category) to the
  // API-style keys (brand_name/form_name/manufacturer_name/category_name) so
  // that apiDetailToFormPayload always receives a consistent shape.
  const normalized: Record<string, unknown> = {
    ...data,
    brand_name: data.brand_name ?? data.brand ?? null,
    form_name: data.form_name ?? data.form ?? null,
    manufacturer_name: data.manufacturer_name ?? data.manufacturer ?? null,
    category_name: data.category_name ?? data.category ?? null,
  };
  const apiDetail = normalized as unknown as ApiProductDetail;
  return {
    id: req.hospital_catalog_product_id || req.request_id,
    sku: (data.sku as string) || req.suggested_sku || '',
    brand: (data.brand_name as string) || (data.brand as string) || req.suggested_brand || '',
    productName: (data.product_name as string) || req.suggested_product_name || '',
    description: (data.description as string) || '',
    form: (data.form_name as string) || (data.form as string) || '',
    status: 'active' as const,
    manufacturer: (data.manufacturer_name as string) || (data.manufacturer as string) || req.suggested_manufacturer || '',
    category: Array.isArray(data.category_name) ? (data.category_name as string[]).join(', ') : ((data.category_name as string) || (data.category as string) || req.suggested_category || ''),
    createdAt: req.created_at,
    updatedAt: req.updated_at,
    // Convert the flat product_data snapshot to the nested ProductRegistrationPayload
    // structure that ProductViewDialog reads via regData.overview.*, regData.nutritions.*, etc.
    registrationData: apiDetailToFormPayload(apiDetail) as unknown as Record<string, unknown>,
  };
});

// Reset form when detail changes
watch(() => store.selectedRequest, (req) => {
  if (req) {
    reviewNotes.value = '';
    internalNotes.value = '';
    approvedProductName.value = req.suggested_product_name || '';
    rejectionReason.value = '';
    rejectionDetail.value = '';
    fieldErrors.value = {};
  }
});

// Close product dialog when notification detail is closed
watch(() => store.showDetail, (visible) => {
  if (!visible) {
    productViewVisible.value = false;
  }
});

const rejectionReasonOptions = REJECTION_REASONS.map((r) => ({
  value: r,
  label: r.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase()),
}));

const request = computed(() => store.selectedRequest);

const { formatDateTime } = useTimezone();

async function handleApprove() {
  fieldErrors.value = {};

  if (!authStore.staffId) {
    fieldErrors.value.submit = 'Unable to identify current user. Please log in again.';
    return;
  }

  if (!reviewNotes.value.trim()) {
    fieldErrors.value.reviewNotes = 'Review notes are required.';
  }

  if (!approvedProductName.value.trim()) {
    fieldErrors.value.approvedProductName = 'Approved product name is required.';
  }

  if (Object.keys(fieldErrors.value).length > 0) return;

  const payload: ActionProductRequest = {
    action: 'approve',
    action_by: authStore.staffId,
    review_notes: reviewNotes.value.trim(),
    approved_product_name: approvedProductName.value.trim(),
    internal_notes: internalNotes.value.trim() || undefined,
  };

  const success = await store.submitAction(request.value!.request_id, payload);
  if (success) {
    toast.success('Product approved successfully.');
  } else {
    fieldErrors.value.submit = store.error || 'Failed to approve request.';
  }
}

async function handleReject() {
  fieldErrors.value = {};

  if (!authStore.staffId) {
    fieldErrors.value.submit = 'Unable to identify current user. Please log in again.';
    return;
  }

  if (!reviewNotes.value.trim()) {
    fieldErrors.value.reviewNotes = 'Review notes are required.';
  }
  if (!rejectionReason.value) {
    fieldErrors.value.rejectionReason = 'Rejection reason is required.';
  }
  if (!rejectionDetail.value.trim()) {
    fieldErrors.value.rejectionDetail = 'Rejection details are required.';
  }

  if (Object.keys(fieldErrors.value).length > 0) return;

  const payload: ActionProductRequest = {
    action: 'reject',
    action_by: authStore.staffId,
    review_notes: reviewNotes.value.trim(),
    rejection_reason: rejectionReason.value as (typeof REJECTION_REASONS)[number],
    rejection_detail: rejectionDetail.value.trim(),
    internal_notes: internalNotes.value.trim() || undefined,
  };

  const success = await store.submitAction(request.value!.request_id, payload);
  if (success) {
    toast.success('Product rejected successfully.');
  } else {
    fieldErrors.value.submit = store.error || 'Failed to reject request.';
  }
}
</script>

<template>
  <!-- Backdrop -->
  <Teleport to="body">
    <transition
      enter-active-class="transition-opacity duration-200"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition-opacity duration-150"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="store.showDetail"
        class="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
        @click.self="store.closeDetail()"
      >
        <!-- Modal -->
        <div class="relative w-full max-w-2xl max-h-[90vh] overflow-y-auto rounded-xl bg-white shadow-2xl dark:bg-gray-800">
          <!-- Loading state -->
          <div
            v-if="store.isLoadingDetail"
            class="flex items-center justify-center py-16"
          >
            <Loader2 class="h-8 w-8 animate-spin text-primary-500" />
          </div>

          <!-- Content -->
          <template v-else-if="request">
            <!-- Header -->
            <div class="sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4 dark:border-gray-700 dark:bg-gray-800">
              <div>
                <h2 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                  {{ request.request_title }}
                </h2>
                <p class="text-sm text-gray-500 dark:text-gray-400">
                  {{ request.request_reference }}
                </p>
              </div>
              <button
                type="button"
                aria-label="Close"
                class="rounded-lg p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700"
                @click="store.closeDetail()"
              >
                <X class="h-5 w-5" />
              </button>
            </div>

            <!-- Request Info -->
            <div class="px-6 py-4 space-y-4">
              <!-- Request metadata -->
              <div class="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-4 dark:bg-gray-900">
                <div>
                  <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Hospital</p>
                  <p class="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{{ request.hospital_name }}</p>
                </div>
                <div>
                  <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Requested At</p>
                  <p class="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{{ formatDateTime(request.requested_at) }}</p>
                </div>
                <div>
                  <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Request Reason</p>
                  <p class="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{{ request.request_reason }}</p>
                </div>
                <div>
                  <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Product Name</p>
                  <p class="mt-0.5 text-sm font-medium text-gray-900 dark:text-gray-100">{{ request.suggested_product_name }}</p>
                </div>
                <div v-if="request.suggested_sku">
                  <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">SKU</p>
                  <p class="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{{ request.suggested_sku }}</p>
                </div>
                <div v-if="request.suggested_brand">
                  <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Brand</p>
                  <p class="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{{ request.suggested_brand }}</p>
                </div>
                <div v-if="request.suggested_manufacturer">
                  <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Manufacturer</p>
                  <p class="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{{ request.suggested_manufacturer }}</p>
                </div>
                <div v-if="request.suggested_category">
                  <p class="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Category</p>
                  <p class="mt-0.5 text-sm text-gray-900 dark:text-gray-100">{{ request.suggested_category }}</p>
                </div>
              </div>

              <!-- Divider -->
              <hr class="border-gray-200 dark:border-gray-700">

              <!-- Action form -->
              <div class="space-y-4">
                <h3 class="text-sm font-semibold text-gray-900 dark:text-gray-100">Review & Action</h3>

                <!-- Review notes (required for approve & reject) -->
                <div>
                  <label
                    for="review-notes"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Review Notes <span class="text-red-500">*</span>
                  </label>
                  <Textarea
                    id="review-notes"
                    v-model="reviewNotes"
                    rows="3"
                    class="mt-1 block w-full"
                    :class="{ 'border-red-500!': fieldErrors.reviewNotes }"
                    placeholder="Enter your review notes..."
                  />
                  <p v-if="fieldErrors.reviewNotes" class="mt-1 text-xs text-red-500">{{ fieldErrors.reviewNotes }}</p>
                </div>

                <!-- Internal notes (optional) -->
                <div>
                  <label
                    for="internal-notes"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Internal Notes <span class="text-xs text-gray-400">(optional, team-only)</span>
                  </label>
                  <Textarea
                    id="internal-notes"
                    v-model="internalNotes"
                    rows="2"
                    class="mt-1 block w-full"
                    placeholder="Internal notes (not visible to tenant)..."
                  />
                </div>

                <!-- Approved product name (for approve) -->
                <div>
                  <label
                    for="approved-product-name"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Approved Product Name <span class="text-red-500">*</span>
                  </label>
                  <InputText
                    id="approved-product-name"
                    v-model="approvedProductName"
                    class="mt-1 block w-full"
                    :class="[
                      fieldErrors.approvedProductName
                        ? 'border-red-500! focus:border-red-500! focus:ring-red-500!'
                        : ''
                    ]"
                    placeholder="Final product name for the catalogue"
                  />
                  <p
                    v-if="fieldErrors.approvedProductName"
                    class="mt-1 text-xs text-red-600 dark:text-red-400"
                  >
                    {{ fieldErrors.approvedProductName }}
                  </p>
                </div>

                <!-- Rejection reason dropdown (for reject) -->
                <div>
                  <label
                    for="rejection-reason"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Rejection Reason <span class="text-xs text-gray-400">(required for rejection only)</span>
                  </label>
                  <Select
                    id="rejection-reason"
                    v-model="rejectionReason"
                    :options="rejectionReasonOptions"
                    option-label="label"
                    option-value="value"
                    placeholder="Select a reason..."
                    :class="[
                      'mt-1 w-full',
                      fieldErrors.rejectionReason ? 'p-invalid' : ''
                    ]"
                  />
                  <p
                    v-if="fieldErrors.rejectionReason"
                    class="mt-1 text-xs text-red-600 dark:text-red-400"
                  >
                    {{ fieldErrors.rejectionReason }}
                  </p>
                </div>

                <!-- Rejection detail (for reject) -->
                <div>
                  <label
                    for="rejection-detail"
                    class="block text-sm font-medium text-gray-700 dark:text-gray-300"
                  >
                    Rejection Details <span class="text-xs text-gray-400">(required for rejection only)</span>
                  </label>
                  <Textarea
                    id="rejection-detail"
                    v-model="rejectionDetail"
                    rows="2"
                    class="mt-1 block w-full"
                    :class="[
                      fieldErrors.rejectionDetail
                        ? 'border-red-500! focus:border-red-500! focus:ring-red-500!'
                        : ''
                    ]"
                    placeholder="Provide details for the rejection..."
                  />
                  <p
                    v-if="fieldErrors.rejectionDetail"
                    class="mt-1 text-xs text-red-600 dark:text-red-400"
                  >
                    {{ fieldErrors.rejectionDetail }}
                  </p>
                </div>

                <!-- Submit error -->
                <p
                  v-if="fieldErrors.submit"
                  class="text-sm text-red-600 dark:text-red-400"
                >
                  {{ fieldErrors.submit }}
                </p>
              </div>
            </div>

            <!-- Footer with action buttons -->
            <div class="sticky bottom-0 flex items-center justify-end gap-3 border-t border-gray-200 bg-gray-50 px-6 py-4 dark:border-gray-700 dark:bg-gray-900">
              <Button
                severity="secondary"
                outlined
                label="Cancel"
                class="rounded-lg! text-sm!"
                @click="store.closeDetail()"
              />
              <Button
                severity="primary"
                class="rounded-lg! text-sm!"
                @click="productViewVisible = true"
              >
                <Eye class="h-4 w-4 mr-1.5" />
                Product Details
              </Button>
              <Button
                severity="danger"
                :disabled="store.isSubmitting"
                :loading="store.isSubmitting"
                class="rounded-lg! text-sm!"
                @click="handleReject"
              >
                <XCircle class="h-4 w-4 mr-1.5" />
                Reject
              </Button>
              <Button
                severity="success"
                :disabled="store.isSubmitting"
                :loading="store.isSubmitting"
                class="rounded-lg! text-sm!"
                @click="handleApprove"
              >
                <CheckCircle class="h-4 w-4 mr-1.5" />
                Approve
              </Button>
            </div>
          </template>
        </div>
      </div>
    </transition>

    <!-- Product View Dialog -->
    <ProductViewDialog
      :visible="productViewVisible"
      :product="productViewData"
      readonly
      @update:visible="productViewVisible = $event"
    />
  </Teleport>
</template>
